import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import appStoreBadge from "../../assets/images/sub/download_test_guide/app_store_badge.png";
import appStoreBadgeMobile from "../../assets/images/sub/download_test_guide/app_store_badge_mobile.png";
import appStoreBadgeEn from "../../assets/images/sub/download_test_guide/apple_badge_desktop_en.png";
import appStoreBadgeEnMobile from "../../assets/images/sub/download_test_guide/apple_badge_mobile_en.png";
import style from "../../assets/scss/sub/downloadTest.module.scss";
import { useLanguage, useUserAgent } from "../../hooks/hooks";
import { IsDesktopBr } from "../common/IsDesktopBr";
import { StrongTextStyle } from "../common/StyledCommonComponents";
import { ArticleTitle } from "./ArticleTitle";
import { MainWrapper } from "./MainWrapper";

enum OSType {
  WINDOW = "window",
  APP = "app",
}

// [2026-07] 검사 진입 라우팅:
//  - Windows / Android → 웹게임(브라우저 검사)
//  - iOS → App Store 앱 (웹게임이 iOS에서 원활하지 않아 네이티브 앱으로 안내)
const APP_STORE_URL =
  "https://apps.apple.com/kr/app/k-pass-%EC%BC%80%EC%9D%B4%ED%8C%A8%EC%8A%A4/id6448768648";
const WEB_GAME_URL = "https://kpass-game.feel-good.io";

export function DownloadTest() {
  const [osType, setOsType] = useState<OSType>(OSType.WINDOW);
  const { isDesktop } = useUserAgent();
  const onClickTabMenu = useCallback((type: OSType) => setOsType(type), []);
  const { isKorean } = useLanguage();
  const { t } = useTranslation("downloadTest");

  const appleImg = isDesktop
    ? isKorean
      ? appStoreBadge
      : appStoreBadgeEn
    : isKorean
    ? appStoreBadgeMobile
    : appStoreBadgeEnMobile;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `검사하기 | ${process.env.REACT_APP_TITLE}`;
  }, []);

  const openWebGame = useCallback(() => {
    window.open(WEB_GAME_URL, "_blank", "noreferrer");
  }, []);

  // 웹 검사(Windows/Android) 공통 주의사항 박스
  const webNoticeBox = (
    <div
      style={{
        width: "100%",
        maxWidth: 680,
        margin: isDesktop ? "36px auto 0" : "28px auto 0",
        padding: isDesktop ? "22px 28px" : "18px 18px",
        boxSizing: "border-box",
        background: "#f6f7f9",
        borderRadius: 14,
        textAlign: "left",
      }}
    >
      <p style={{ fontWeight: 700, fontSize: isDesktop ? 16 : 15, color: "#111827", margin: "0 0 12px" }}>
        ⚠ {t("webNotice.title")}
      </p>
      <ul
        style={{
          margin: 0,
          paddingLeft: 20,
          color: "#4b5563",
          fontSize: isDesktop ? 15 : 14,
          lineHeight: 1.9,
          wordBreak: "keep-all",
        }}
      >
        {(t("webNotice.items", { returnObjects: true }) as unknown as string[]).map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    </div>
  );

  // 권장 사양 박스(저사양 멈춤 고지)
  const webSpecBox = (
    <div
      style={{
        width: "100%",
        maxWidth: 680,
        margin: isDesktop ? "16px auto 0" : "14px auto 0",
        padding: isDesktop ? "22px 28px" : "18px 18px",
        boxSizing: "border-box",
        background: "#fff7ed",
        border: "1px solid #fed7aa",
        borderRadius: 14,
        textAlign: "left",
      }}
    >
      <p style={{ fontWeight: 700, fontSize: isDesktop ? 16 : 15, color: "#9a3412", margin: "0 0 8px" }}>
        💻 {t("webSpec.title")}
      </p>
      <p
        style={{
          fontSize: isDesktop ? 14 : 13,
          color: "#7c2d12",
          lineHeight: 1.6,
          margin: "0 0 12px",
          wordBreak: "keep-all",
          whiteSpace: "pre-line",
        }}
      >
        {t("webSpec.desc")}
      </p>
      <ul
        style={{
          margin: 0,
          paddingLeft: 20,
          color: "#7c2d12",
          fontSize: isDesktop ? 14 : 13,
          lineHeight: 1.9,
          wordBreak: "keep-all",
        }}
      >
        {(t("webSpec.items", { returnObjects: true }) as unknown as string[]).map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    </div>
  );

  const introText = (
    <p
      style={{
        textAlign: "center",
        color: "#4b5563",
        fontSize: isDesktop ? 17 : 15,
        lineHeight: 1.6,
        margin: isDesktop ? "8px 16px 28px" : "6px 16px 22px",
        wordBreak: "keep-all",
      }}
    >
      {t("webIntro")}
    </p>
  );

  const GuideNode = useCallback(() => {
    switch (osType) {
      // Windows 용 탭 → 웹게임(브라우저 검사)
      case OSType.WINDOW:
        return (
          <div className={style.guideContent}>
            {introText}
            <button className={style.downloadBtn} onClick={openWebGame}>
              <Trans t={t} i18nKey={"downloadforWindows"} components={[<IsDesktopBr />, <StrongTextStyle />]} />
            </button>
            {webNoticeBox}
            {webSpecBox}
          </div>
        );
      // Android / iOS 용 탭 → iOS는 App Store 앱, Android는 웹게임
      case OSType.APP:
        return (
          <div className={style.guideContent}>
            <p
              style={{
                textAlign: "center",
                color: "#4b5563",
                fontSize: isDesktop ? 17 : 15,
                lineHeight: 1.6,
                margin: isDesktop ? "8px 16px 28px" : "6px 16px 22px",
                wordBreak: "keep-all",
              }}
            >
              {t("appTabDesc")}
            </p>
            <div className={style.googlePlayGuide}>
              <div
                className={style.badges}
                style={{ display: "flex", gap: 16, alignItems: "stretch", justifyContent: "center", flexWrap: "wrap" }}
              >
                {/* iOS → App Store 앱 (배지는 원래 크기 유지) */}
                <a
                  href={APP_STORE_URL}
                  target={"_blank"}
                  rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center" }}
                >
                  <img className={style.badgeImage} src={appleImg} alt={"App Store"} style={{ display: "block" }} />
                </a>
                {/* Android → 웹게임. 배지 높이에 맞춰 늘어남(alignSelf stretch), 단독일 땐 minHeight */}
                <button
                  type="button"
                  onClick={openWebGame}
                  style={{
                    alignSelf: "stretch",
                    minHeight: isDesktop ? 52 : 46,
                    padding: isDesktop ? "0 28px" : "0 22px",
                    borderRadius: 999,
                    border: "none",
                    background: "#1f4e78",
                    color: "#fff",
                    fontSize: isDesktop ? 16 : 15,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {t("androidWebButton")}
                </button>
              </div>
            </div>
            {webNoticeBox}
            {webSpecBox}
          </div>
        );
    }
    // eslint-disable-next-line
  }, [osType, isDesktop, isKorean]);

  return (
    <>
      <MainWrapper showFooter={true} mainClassName={style.downloadTest}>
        <>
          <div className={style.bgItem1}></div>
          <div className={style.bgItem2}></div>
          <div className={style.title}>
            <ArticleTitle title={t("download")} className={""} />
          </div>
          <div className={style.tabMenu}>
            <button
              className={`${style.tab} ${osType === OSType.WINDOW ? style.active : ""}`}
              onClick={() => onClickTabMenu(OSType.WINDOW)}
            >
              {t("forWindows")}
            </button>
            <button
              className={`${style.tab} ${osType === OSType.APP ? style.active : ""}`}
              onClick={() => onClickTabMenu(OSType.APP)}
            >
              {t("forAndroidIos")}
            </button>
          </div>
          <GuideNode />
        </>
      </MainWrapper>
    </>
  );
}
